import { StoryModel } from '../models/story.model';
import { uniq } from 'lodash';
import { uuid } from '../../shared/uuid';
import { Environment } from 'src/environments/environment.model';
import { mergeSceneGraphs, updateChapterShowSelectPickThroughObjects } from './merge-scene-graph';
import { updateParticleSystems } from './merge-particle-systems';

import {mergeColorRecipes, updateChapterColorRecipes} from './merge-color-recipes';
import { mergeReflections } from './merge-reflections';
import { mergeAssetLibraries } from './merge-asset-libraries';
import { setLightsForChapters } from './merge-lights';


export function mergeModules(
  currentStory: StoryModel,
  currentStoryId: string,
  newStory: StoryModel,
  newStoryId: string,
  exportType: Environment['exportType'] = 'bookmark',
  prefixUuidFunc: uuid.PrefixUuidFunc = uuid.idWithPrefix
): StoryModel {

  // start by merging global properties
  const {
    sceneGraph,
    graphsMerged,
    objectIdsUniqueToNew,
    objectIdsUniqueToOriginal
  } = mergeSceneGraphs(
    currentStory.sceneGraph,
    newStory.sceneGraph
  );

  const assetLibraries = mergeAssetLibraries(
    currentStory.assetLibraries,
    newStory.assetLibraries
  );

  const reflections = mergeReflections(
    currentStory.reflections,
    newStory.reflections,
    currentStoryId,
    newStoryId
  );

  const { colorRecipes, newColorRecipeIds } = mergeColorRecipes(
    currentStory.colorRecipes,
    newStory.colorRecipes,
    prefixUuidFunc
  );

  // then merge chapter properties

  const currentChaptersUpdatedLights = setLightsForChapters(
    currentStory.chapters,
    currentStory.assetLibraries.lights
  );

  const newChaptersUpdatedLights = setLightsForChapters(
    newStory.chapters,
    newStory.assetLibraries.lights
  );
  
  const currentChaptersUpdatedProps = updateChapterShowSelectPickThroughObjects(
    currentChaptersUpdatedLights,
    graphsMerged,
    objectIdsUniqueToNew
  );

  const newChaptersUpdatedProps = updateChapterShowSelectPickThroughObjects(
    newChaptersUpdatedLights,
    graphsMerged,
    objectIdsUniqueToOriginal
  );
  
  // give imported chapters a new id
  const importedChaptersWithNewIds = newChaptersUpdatedProps.map(c => {
    return { ...c, id: prefixUuidFunc('chapter') };
  });

  const currentParticleSystemUpdates = updateParticleSystems(
    currentChaptersUpdatedProps,
    (currentStory.customData || {}).particleSystems || []
  );

  const importedParticleSystemUpdates = updateParticleSystems(
    importedChaptersWithNewIds,
    (newStory.customData || {}).particleSystems || []
  );

  const importedChaptersWithUpdatedColorRecipes = updateChapterColorRecipes(
    importedParticleSystemUpdates.chapters,
    newColorRecipeIds
  );

  const chapters = [
    ...currentParticleSystemUpdates.chapters,
    ...importedChaptersWithUpdatedColorRecipes
  ];


  // TODO: what types of customData besides ParticleSystems am I throwing out from imported modules?
  const customData = {
    ...currentStory.customData,
    particleSystems: [
      ...currentParticleSystemUpdates.particleSystemsForCustomData,
      ...importedParticleSystemUpdates.particleSystemsForCustomData
    ]
  };

  const tags = exportType === 'module' ? currentStory.tags : [];

  return {
    ...currentStory,
    assetLibraries,
    chapters,
    sceneGraph,
    customData,
    tags,
    reflections,
    colorRecipes,
    stateId: uniq([
      ...currentStory.stateId,
      ...newStory.stateId
    ])
  };
}
